// ABOUTME: Modal showing evidence source with interview context
// ABOUTME: Displays question, answer, learner context for traceability

import { Fragment } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { Link } from 'react-router-dom';
import { useEvidenceSource } from '@/hooks/useEvidence';
import { TranscriptView } from '@/components/interviews/TranscriptView';

interface EvidenceSourceModalProps {
  evidenceId: string | null;
  isOpen: boolean;
  onClose: () => void;
}

export const EvidenceSourceModal: React.FC<EvidenceSourceModalProps> = ({
  evidenceId,
  isOpen,
  onClose,
}) => {
  const { data, isLoading } = useEvidenceSource(evidenceId);

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        {/* Backdrop */}
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25 backdrop-blur-sm" />
        </Transition.Child>

        {/* Modal */}
        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-5xl transform overflow-hidden rounded-2xl bg-white shadow-xl transition-all max-h-[90vh] flex flex-col">
                <div className="p-6 border-b border-gray-200">
                  <Dialog.Title className="text-2xl font-bold text-gray-900">
                    Evidence Traceability
                  </Dialog.Title>
                </div>

                <div className="flex-1 overflow-y-auto p-6">
                  {isLoading ? (
                    <div className="py-8 text-center">
                      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto"></div>
                      <p className="mt-4 text-gray-600">Loading evidence source...</p>
                    </div>
                  ) : data ? (
                    <div className="space-y-6">
                      {/* Evidence Quote - Large Display */}
                      <div className="bg-gradient-to-r from-blue-500 to-blue-600 p-6 rounded-xl text-white shadow-lg">
                        <div className="flex items-start gap-3">
                          <svg className="w-8 h-8 flex-shrink-0 opacity-75" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h3.983v10h-9.983z" />
                          </svg>
                          <div className="flex-1">
                            <h3 className="text-sm font-semibold uppercase tracking-wide mb-2 opacity-90">Evidence Quote</h3>
                            <p className="text-xl leading-relaxed italic">{data.quote}</p>
                            <div className="mt-4 flex gap-4 text-sm">
                              <span className="bg-white/20 px-3 py-1 rounded-full">
                                <span className="font-medium">Type:</span> {data.evidence_type}
                              </span>
                              <span className="bg-white/20 px-3 py-1 rounded-full">
                                <span className="font-medium">Strength:</span> {data.strength}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Interview Details */}
                      <div className="bg-gray-50 rounded-xl p-5 border border-gray-200">
                        <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                          Interview Details
                        </h3>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                          <div>
                            <span className="text-xs font-medium text-gray-500 uppercase tracking-wide block mb-1">Interview ID</span>
                            <p className="text-gray-900 font-mono text-sm">{data.interview_id}</p>
                          </div>
                          {data.interview_date && (
                            <div>
                              <span className="text-xs font-medium text-gray-500 uppercase tracking-wide block mb-1">Date</span>
                              <p className="text-gray-900">{data.interview_date}</p>
                            </div>
                          )}
                          {data.research_area && (
                            <div>
                              <span className="text-xs font-medium text-gray-500 uppercase tracking-wide block mb-1">Research Area</span>
                              <p className="text-gray-900">{data.research_area}</p>
                            </div>
                          )}
                          {data.facilitator && (
                            <div>
                              <span className="text-xs font-medium text-gray-500 uppercase tracking-wide block mb-1">Facilitator</span>
                              <p className="text-gray-900">{data.facilitator}</p>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Participant Context */}
                      {(data.learner_group || data.learner_institution) && (
                        <div className="bg-purple-50 rounded-xl p-5 border border-purple-200">
                          <h3 className="text-lg font-bold text-purple-900 mb-4 flex items-center gap-2">
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                            </svg>
                            Participant Context
                          </h3>
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            {data.learner_group && (
                              <div className="bg-white rounded-lg p-3 border border-purple-200">
                                <span className="text-xs font-medium text-purple-700 uppercase tracking-wide block mb-1">Group</span>
                                <p className="text-purple-900 font-medium">{data.learner_group}</p>
                              </div>
                            )}
                            {data.learner_institution && (
                              <div className="bg-white rounded-lg p-3 border border-purple-200">
                                <span className="text-xs font-medium text-purple-700 uppercase tracking-wide block mb-1">Institution</span>
                                <p className="text-purple-900 font-medium">{data.learner_institution}</p>
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Full Interview Transcript */}
                      {data.full_transcript && data.full_transcript.length > 0 && (
                        <div className="bg-white rounded-xl border-2 border-gray-200 overflow-hidden">
                          <div className="bg-gray-100 px-5 py-3 border-b border-gray-200">
                            <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                              </svg>
                              Full Interview Transcript
                            </h3>
                            <p className="text-sm text-gray-600 mt-1">
                              {data.full_transcript.length} question-answer pairs
                            </p>
                          </div>
                          <div className="p-5">
                            <TranscriptView qa={data.full_transcript} highlightQuestionNumber={data.question_number} />
                          </div>
                        </div>
                      )}
                    </div>
                  ) : null}
                </div>

                {/* Footer with Close Button */}
                <div className="p-6 border-t border-gray-200 bg-gray-50">
                  <div className="flex justify-between items-center">
                    {data ? (
                      <Link
                        to={`/dashboard/interviews/${data.interview_id}`}
                        onClick={onClose}
                        className="text-sm font-medium text-alx-blue hover:underline"
                      >
                        Open full interview →
                      </Link>
                    ) : (
                      <span />
                    )}
                    <button
                      onClick={onClose}
                      className="px-6 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-800 transition-colors font-medium"
                    >
                      Close
                    </button>
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};
